import React, { useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Text,
  Box,
  Divider,
  Collapse,
  Button,
  Group,
  Modal,
  Stack,
  Input,
  UnstyledButton,
} from '@mantine/core';
import { createStyles } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { DatePicker } from '@mantine/dates';
import { IconChevronDown } from '@tabler/icons-react';
import dayjs from 'dayjs';
import BookingEditModal from './BookingEditModal';

const FILTER_OPTIONS = [
  { value: 'next30', label: 'Next 30 days' },
  { value: 'next90', label: 'Next 90 days' },
  { value: 'next365', label: 'Next 365 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last365', label: 'Last 365 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const rangesOverlap = (startA, endA, startB, endB) => {
  if (!startA || !endA || !startB || !endB) return true;
  return startA <= endB && endA >= startB;
};

const useStyles = createStyles((theme) => ({
  booked: {
    backgroundColor: theme.colors.red[1],
    borderColor: theme.colors.red[4],
  },
  closed: {
    backgroundColor: theme.colors.gray[1],
    borderColor: theme.colors.gray[4],
  },
  inProcess: {
    backgroundColor: theme.colors.green[1],
    borderColor: theme.colors.green[4],
  },
  title: {
    fontSize: theme.fontSizes.lg,
    marginBottom: theme.spacing.md,
  },
  bookingContainer: {
    marginBottom: theme.spacing.md,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.gray[4]}`,
  },
  bookingHeader: {
    marginBottom: theme.spacing.xs,
    cursor: 'pointer',
  },
  bookingDetails: {
    marginTop: theme.spacing.sm,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 500,
    marginBottom: theme.spacing.xs,
  },
  sectionContent: {
    fontSize: theme.fontSizes.sm,
  },
  detailsToggle: {
    '&:hover': {
      backgroundColor: theme.colors.green[10],
      color: theme.white,
    },
  },
  filterRoot: {
    position: 'relative',
    alignSelf: 'flex-start',
    width: 260,
  },
  filterTrigger: {
    display: 'block',
    width: '100%',
    minHeight: 36,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[8]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
    fontSize: theme.fontSizes.sm,
    '&:hover': {
      borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5],
    },
  },
  filterDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 300,
    minWidth: '100%',
    width: 'max-content',
    maxWidth: 'min(100vw - 32px, 300px)',
    padding: theme.spacing.xs,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    boxShadow: theme.shadows.sm,
  },
  filterOption: {
    display: 'block',
    width: '100%',
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    marginBottom: 2,
    borderRadius: theme.radius.sm,
    fontSize: theme.fontSizes.sm,
    lineHeight: 2,
    textAlign: 'left',
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:last-of-type': {
      marginBottom: 0,
    },
    '&:hover': {
      backgroundColor: theme.colors.gray[5],
      color: theme.black,
    },
  },
  filterOptionActive: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.colors.gray[5],
      color: theme.black,
    },
  },
}));

const BookingList = ({ dateStates, handleAccept, handleDecline, handleStop, handleUpdateBooking }) => {
  const { classes } = useStyles();
  const [openedBookings, setOpenedBookings] = useState({});
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', key: '' });
  const [filterPreset, setFilterPreset] = useState('next30');
  const [customRange, setCustomRange] = useState([null, null]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useClickOutside(() => setFilterOpen(false));
  const [editModal, setEditModal] = useState({ opened: false, key: null, booking: null });

  const currentDate = dayjs().format('YYYY-MM-DD');

  const filterWindow = useMemo(() => {
    const today = dayjs().startOf('day');
    switch (filterPreset) {
      case 'next30':
        return { start: today.format('YYYY-MM-DD'), end: today.add(30, 'day').format('YYYY-MM-DD') };
      case 'next90':
        return { start: today.format('YYYY-MM-DD'), end: today.add(90, 'day').format('YYYY-MM-DD') };
      case 'next365':
        return { start: today.format('YYYY-MM-DD'), end: today.add(365, 'day').format('YYYY-MM-DD') };
      case 'last30':
        return { start: today.subtract(30, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
      case 'last90':
        return { start: today.subtract(90, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
      case 'last365':
        return { start: today.subtract(365, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
      case 'custom': {
        const [a, b] = customRange || [];
        if (!a || !b) return null;
        const s = dayjs(a).format('YYYY-MM-DD');
        const e = dayjs(b).format('YYYY-MM-DD');
        return s <= e ? { start: s, end: e } : { start: e, end: s };
      }
      case 'all':
      default:
        return null;
    }
  }, [filterPreset, customRange]);

  const filterDisplayLabel = useMemo(() => {
    if (filterPreset === 'custom') {
      const [a, b] = customRange || [];
      if (a && b) {
        return `${dayjs(a).format('MMM D, YYYY')} – ${dayjs(b).format('MMM D, YYYY')}`;
      }
      return 'Custom — pick dates';
    }
    return FILTER_OPTIONS.find((o) => o.value === filterPreset)?.label ?? 'Next 30 days';
  }, [filterPreset, customRange]);

  const handleFilterPresetSelect = (value) => {
    setFilterPreset(value);
    if (value !== 'custom') {
      setFilterOpen(false);
    }
  };

  const handleCustomRangeChange = (range) => {
    setCustomRange(range);
    const [a, b] = range || [];
    if (a && b) {
      setFilterOpen(false);
    }
  };

  const entriesFiltered = useMemo(() => {
    const states = dateStates && typeof dateStates === 'object' ? dateStates : {};
    const all = Object.entries(states);
    if (!filterWindow) return all;
    return all.filter(([, booking]) =>
      rangesOverlap(booking.start, booking.end, filterWindow.start, filterWindow.end)
    );
  }, [dateStates, filterWindow]);

  const toggleBookingDetails = (key) => {
    setOpenedBookings((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const openConfirmModal = (type, key) => {
    setConfirmModal({ isOpen: true, type, key });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: '', key: '' });
  };

  const handleConfirmAction = () => {
    const { type, key } = confirmModal;
    if (type === 'accept') {
      handleAccept(key);
    } else if (type === 'decline') {
      handleDecline(key);
    } else if (type === 'stop') {
      handleStop(key);
    }
    closeConfirmModal();
  };

  const openEdit = (e, key, booking) => {
    e.stopPropagation();
    setEditModal({ opened: true, key, booking });
  };

  const closeEdit = () => {
    setEditModal({ opened: false, key: null, booking: null });
  };

  const futureBookings = entriesFiltered
    .filter(([, booking]) => booking.end >= currentDate)
    .sort((a, b) => new Date(a[1].start) - new Date(b[1].start));

  const pastBookings = entriesFiltered
    .filter(([, booking]) => booking.end < currentDate)
    .sort((a, b) => new Date(b[1].start) - new Date(a[1].start));

  const renderBookingItem = (key, booking) => {
    const isOpened = openedBookings[key];
    let boxClass;

    switch (booking.booked) {
      case 'booked':
        boxClass = classes.booked;
        break;
      case 'closed':
        boxClass = classes.closed;
        break;
      case 'inProcess':
        boxClass = classes.inProcess;
        break;
      default:
        boxClass = '';
        break;
    }

    return (
      <Paper key={key} className={`${classes.bookingContainer} ${boxClass}`}>
        <Group position="apart" className={classes.bookingHeader} onClick={() => toggleBookingDetails(key)}>
          <Text color="resirent" weight={600} size="md">
            {(() => {
              const source = booking.formDate?.bookingSource || '';
              const guests = booking.formDate?.numberOfGuests ? `${booking.formDate.numberOfGuests}P` : '';

              const extras = [
                booking.formDate?.hasBaby || booking.formDate?.babycod ? '+ baby' : '',
                booking.formDate?.hasDog || (booking.formDate?.numberOfDogs > 0) ? '+ dog' : '',
                booking.formDate?.hasParking || booking.formDate?.parking ? '+ parking' : '',
              ]
                .filter(Boolean)
                .join(' ');

              const fallbackLabel = [source, guests, extras].filter(Boolean).join(' ').trim();

              const displayLabel =
                booking.label && booking.label.trim() !== '' ? booking.label : fallbackLabel;

              return booking.booked === 'inProcess' ? (
                <>
                  Request: {displayLabel} ({booking.start} - {booking.end})
                </>
              ) : (
                <>
                  {booking.booked}: {displayLabel} ({booking.start} - {booking.end})
                </>
              );
            })()}
          </Text>
          <Button
            variant="subtle"
            color="resirent"
            compact
            className={classes.detailsToggle}
            onClick={(e) => {
              e.stopPropagation();
              toggleBookingDetails(key);
            }}
          >
            {isOpened ? 'Hide Details' : 'Show Details'}
          </Button>
        </Group>
        <Collapse in={isOpened} className={classes.bookingDetails}>
          <Divider my="sm" />
          <Text className={classes.sectionTitle}>Details:</Text>
          <Box className={classes.sectionContent}>
            <Text>
              <strong>Status:</strong> {booking.booked}
            </Text>
            {booking.details?.bookingSource !== undefined && (
              <Text>
                <strong>Booking Source:</strong> {booking.details?.bookingSource || 'N/A'}
              </Text>
            )}
            <Text>
              <strong>Check-in:</strong> {booking.start}
            </Text>
            <Text>
              <strong>Check-out:</strong> {booking.end}
            </Text>
            <Text>
              <strong>Number of Guests:</strong> {booking.formDate?.numberOfGuests ?? 'N/A'}
            </Text>
            {(booking.formDate?.hasParking !== undefined || booking.formDate?.parking !== undefined) && (
              <Text>
                <strong>Parking:</strong>{' '}
                {booking.formDate.hasParking || booking.formDate.parking ? 'Yes' : 'No'}
              </Text>
            )}
            {(booking.formDate?.hasDog !== undefined || booking.formDate?.numberOfDogs > 0) && (
              <Text>
                <strong>Dog:</strong>{' '}
                {booking.formDate.hasDog || booking.formDate?.numberOfDogs > 0 ? 'Yes' : 'No'}
              </Text>
            )}
            {booking.formDate?.numberOfDogs !== undefined && (
              <Text>
                <strong>Number of Dogs:</strong> {booking.formDate.numberOfDogs}
              </Text>
            )}
            {(booking.formDate?.hasBaby !== undefined || booking.formDate?.babycod !== undefined) && (
              <Text>
                <strong>Baby:</strong> {booking.formDate.hasBaby || booking.formDate.babycod ? 'Yes' : 'No'}
              </Text>
            )}
            {booking.formDate?.specialWishes && (
              <Text>
                <strong>Special Wishes:</strong> {booking.formDate.specialWishes}
              </Text>
            )}
            {booking.formDate?.underCityTaxAge !== undefined && (
              <Text>
                <strong>Under City Tax Age:</strong> {booking.formDate.underCityTaxAge}
              </Text>
            )}
            {booking.formDate?.breakfast !== undefined && (
              <Text>
                <strong>Breakfast:</strong> {booking.formDate.breakfast ? 'Yes' : 'No'}
              </Text>
            )}
            {booking.formDate?.email && (
              <Text>
                <strong>Email:</strong> {booking.formDate.email}
              </Text>
            )}
            {booking.formDate?.message && (
              <Text>
                <strong>Message:</strong> {booking.formDate.message}
              </Text>
            )}
          </Box>
          <Group mt="md">
            <Button color="green" onClick={() => openConfirmModal('accept', key)}>
              Accept
            </Button>
            <Button color="gray.600" onClick={(e) => openEdit(e, key, booking)}>
              Edit
            </Button>
            <Button color="red" onClick={() => openConfirmModal('decline', key)}>
              Decline
            </Button>
          </Group>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Container size="xl" mt="xl">
      <Stack spacing="md" mb="lg">
        <Text weight={600}>Filter bookings by date range</Text>
        <Box ref={filterRef} className={classes.filterRoot}>
          <Input.Wrapper label="Show bookings overlapping">
            <UnstyledButton
              type="button"
              className={classes.filterTrigger}
              aria-expanded={filterOpen}
              aria-haspopup="listbox"
              onClick={() => setFilterOpen((open) => !open)}
            >
              <Group position="apart" spacing="xs" noWrap>
                <Text size="sm" truncate>
                  {filterDisplayLabel}
                </Text>
                <IconChevronDown size={14} stroke={1.5} />
              </Group>
            </UnstyledButton>
          </Input.Wrapper>
          {filterOpen && (
            <Paper className={classes.filterDropdown} withBorder={false}>
              <Stack spacing={2}>
                {FILTER_OPTIONS.map((opt) => (
                  <UnstyledButton
                    key={opt.value}
                    type="button"
                    className={`${classes.filterOption} ${
                      filterPreset === opt.value ? classes.filterOptionActive : ''
                    }`}
                    onClick={() => handleFilterPresetSelect(opt.value)}
                  >
                    {opt.label}
                  </UnstyledButton>
                ))}
                {filterPreset === 'custom' && (
                  <>
                    <Divider my="xs" />
                    <DatePicker
                      type="range"
                      value={customRange}
                      onChange={handleCustomRangeChange}
                    />
                  </>
                )}
              </Stack>
            </Paper>
          )}
        </Box>
        {filterPreset === 'custom' && filterWindow === null && (
          <Text size="sm" color="dimmed">
            Select a start and end date to filter, or choose another preset.
          </Text>
        )}
      </Stack>

      <Text size="xl" weight={700} align="center" mb="xl" className={classes.title}>
        Future Bookings
      </Text>
      {futureBookings.length === 0 ? (
        <Text align="center" color="dimmed" mb="md">
          No bookings in this period.
        </Text>
      ) : (
        futureBookings.map(([key, booking]) => renderBookingItem(key, booking))
      )}
      <Divider my="xl" />
      <Text size="xl" weight={700} align="center" mb="xl" className={classes.title}>
        Past Bookings
      </Text>
      {pastBookings.length === 0 ? (
        <Text align="center" color="dimmed">
          No bookings in this period.
        </Text>
      ) : (
        pastBookings.map(([key, booking]) => renderBookingItem(key, booking))
      )}

      <Modal
        opened={confirmModal.isOpen}
        onClose={closeConfirmModal}
        title={`Confirm ${confirmModal.type.charAt(0).toUpperCase() + confirmModal.type.slice(1)} Booking`}
      >
        <Text>Are you sure you want to {confirmModal.type} this booking?</Text>
        <Group position="center" mt="md">
          <Button color="red" onClick={handleConfirmAction}>
            Yes
          </Button>
          <Button variant="default" onClick={closeConfirmModal}>
            No
          </Button>
        </Group>
      </Modal>

      <BookingEditModal
        opened={editModal.opened}
        onClose={closeEdit}
        rangeKey={editModal.key}
        booking={editModal.booking}
        onSave={handleUpdateBooking}
      />
    </Container>
  );
};

export default BookingList;
